import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {useEffect} from 'react';
import {useGetConfigurationCollections} from '@src/api/configurations';
import LoadingMessage from '@src/components/common/LoadingMessage.tsx';
import {notifyWarning} from '@src/components/notifier/notify.tsx';
import {ErrorMessage} from '@src/components/ui/message-container.tsx';
import {useMapExtentHandler} from '@src/hooks/useMapExtentHandler.ts';
import useVisualizationParams from '@src/hooks/useVisualizationParams';
import dimensions from '@src/styles/dimensions.ts';

const CollectionSelection: React.FC = () => {
  const {configurationId, collectionId: selectedCollectionId} =
    useVisualizationParams();
  const {collections, isLoading, isError, error, failedCollections} =
    useGetConfigurationCollections(configurationId);

  const failedIds = failedCollections?.map(c => c).join(',');
  useEffect(() => {
    if (failedIds && !isLoading) {
      notifyWarning({
        data: {
          title: 'Failed to load collections',
          message: failedIds,
        },
        toastId: failedIds,
      });
    }
  }, [failedIds, isLoading]);

  const handleCollectionChange = useMapExtentHandler();

  if (isLoading) {
    return (
      <LoadingMessage rootProps={{sx: {blockSize: 'auto', paddingBlock: 2}}} />
    );
  }

  if (isError && error) {
    return (
      <ErrorMessage>
        <Typography variant="h6" color="inherit" gutterBottom>
          {error.message}
        </Typography>
      </ErrorMessage>
    );
  }

  return (
    <Box
      sx={{
        marginInline: `${2 * dimensions.leftSidebar.contentMargin}px`,
        paddingBlockEnd: 2,
        paddingBlockStart: 0.5,
      }}
    >
      <Typography
        component="label"
        sx={{fontSize: 12, color: 'text.secondary', paddingBlockEnd: 1}}
        id="collections-radio-buttons-group-label"
      >
        Data Collection
      </Typography>
      <RadioGroup
        aria-labelledby="collections-radio-buttons-group-label"
        value={selectedCollectionId}
        onChange={(_e, id) => handleCollectionChange(id, '')}
        name="radio-buttons-group"
        sx={{
          marginInlineStart: 0.25,
        }}
      >
        {collections.map(collection => {
          const colId = collection.id;
          const collectionName = collection.title ?? colId;
          return (
            <Box
              key={colId}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <FormControlLabel
                value={colId}
                control={<Radio />}
                label={
                  <Typography variant="caption" sx={{paddingInlineStart: 1}}>
                    {collectionName}
                  </Typography>
                }
              />
            </Box>
          );
        })}
      </RadioGroup>
    </Box>
  );
};

export default CollectionSelection;
